import React, { useState, useEffect } from "react";
import { createMultipleLoadingUnloadingRecords } from "../services/deliveryService"; // Importing the service function
import toast from "react-hot-toast";
import { Link } from "react-router-dom"; // Import Link for navigation

const ProductLoadingUnloading = () => {
  // State for storing the loading/unloading info for both products
  const [deliveries, setDeliveries] = useState([
    { product_id: 1, quantity_loaded: "", quantity_unloaded: "" }, // Cool Bottle
    { product_id: 2, quantity_loaded: "", quantity_unloaded: "" }, // Pet Bottle
  ]);

  // State for the current date
  const [date, setDate] = useState("");

  // State to toggle between Loading and Unloading
  const [isLoading, setIsLoading] = useState(true); // true for Loading, false for Unloading

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]; // Get today's date in YYYY-MM-DD format
    setDate(today); // Set date to today
  }, []);

  // Handle form input change for both loading and unloading
  const handleChange = (index, e) => {
    const updatedDeliveries = [...deliveries];
    updatedDeliveries[index][e.target.name] = e.target.value;
    setDeliveries(updatedDeliveries);
  };

  // Handle form submission for loading or unloading records
  const handleSubmit = async (e, type) => {
    e.preventDefault();

    const isValid = deliveries.every(
      (delivery) =>
        (type === "loading" && delivery.quantity_loaded) ||
        (type === "unloading" && delivery.quantity_unloaded)
    );

    if (!isValid) {
      toast.error(`Please fill all fields for ${type}.`);
      return;
    }

    try {
      // Prepare the data for submission based on the type (loading or unloading)
      const records = deliveries.map((delivery) => ({
        product_id: delivery.product_id,
        date: date,
        quantity_loaded: type === "loading" ? delivery.quantity_loaded : 0,
        quantity_unloaded:
          type === "unloading" ? delivery.quantity_unloaded : 0,
      }));

      // Submit the data to the backend
      await createMultipleLoadingUnloadingRecords(records);
      toast.success(
        `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } records successfully created!`
      );
    } catch (error) {
      console.error(`Error creating ${type} records:`, error);
      toast.error(`Failed to create ${type} records.`);
    }
  };

  return (
    <div className="container mx-auto p-6 bg-gray-50 rounded-xl shadow-lg max-w-3xl">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
        Loading/Unloading
      </h2>

      {/* Date Display */}
      <div className="flex flex-col items-center mb-6">
        <label className="text-sm font-medium text-gray-600">Date:</label>
        <span className="text-xl font-semibold text-blue-600">
          {date} (Today)
        </span>
      </div>

      {/* Toggle Button for Loading/Unloading */}
      <div className="flex justify-center mb-8">
        <button
          onClick={() => setIsLoading(true)}
          className={`px-6 py-3 mr-4 rounded-md text-white ${
            isLoading ? "bg-blue-600" : "bg-gray-500"
          } hover:bg-blue-700`}
        >
          Loading
        </button>
        <button
          onClick={() => setIsLoading(false)}
          className={`px-6 py-3 rounded-md text-white ${
            !isLoading ? "bg-green-600" : "bg-gray-500"
          } hover:bg-green-700`}
        >
          Unloading
        </button>
      </div>

      {/* Conditional Form Display based on Toggle */}
      {isLoading ? (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">Loading</h3>
          <form onSubmit={(e) => handleSubmit(e, "loading")}>
            {deliveries.map((delivery, index) => (
              <div
                key={index}
                className="bg-white shadow-md rounded-lg p-6 mb-4"
              >
                <div className="mb-4">
                  <label
                    htmlFor={`quantity_loaded-${index}`}
                    className="block text-sm font-medium text-gray-700"
                  >
                    Quantity Loaded (Product{" "}
                    {delivery.product_id === 1 ? "Cool" : "Pet"} Bottle):
                  </label>
                  <input
                    type="number"
                    name="quantity_loaded"
                    id={`quantity_loaded-${index}`}
                    value={delivery.quantity_loaded}
                    onChange={(e) => handleChange(index, e)}
                    className="w-full p-3 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            ))}
            <div className="flex justify-center">
              <button
                type="submit"
                className="px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all"
              >
                Submit Loading
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-700 mb-4">
            Unloading
          </h3>
          <form onSubmit={(e) => handleSubmit(e, "unloading")}>
            {deliveries.map((delivery, index) => (
              <div
                key={index}
                className="bg-white shadow-md rounded-lg p-6 mb-4"
              >
                <div className="mb-4">
                  <label
                    htmlFor={`quantity_unloaded-${index}`}
                    className="block text-sm font-medium text-gray-700"
                  >
                    Quantity Unloaded (Product{" "}
                    {delivery.product_id === 1 ? "Cool" : "Pet"} Bottle):
                  </label>
                  <input
                    type="number"
                    name="quantity_unloaded"
                    id={`quantity_unloaded-${index}`}
                    value={delivery.quantity_unloaded}
                    onChange={(e) => handleChange(index, e)}
                    className="w-full p-3 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
              </div>
            ))}
            <div className="flex justify-center">
              <button
                type="submit"
                className="px-6 py-3 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all"
              >
                Submit Unloading
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Button to view loading/unloading records */}
      <div className="flex justify-center mt-8">
        <Link to="/view-records">
          <button className="px-6 py-3 text-white bg-gray-600 rounded-lg hover:bg-gray-700 transition-all">
            View Loading/Unloading Records
          </button>
        </Link>
      </div>
    </div>
  );
};

export default ProductLoadingUnloading;
